import { useCallback, useEffect, useMemo, useRef } from 'react';
import cn from 'clsx';
import { type FilterChip } from '@pkgs/components/utils/aiSearchChipUtils';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useAutoCompleteStore } from '@pkgs/components/hooks/stores/useAutoCompleteStore';
import { usePromptStore } from '@pkgs/components/hooks/stores/search/usePromptStore';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import { type ModalState } from '@ev-tech/ui-core-components/Modal';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { CrossNakedIcon as CloseIcon } from '@ev-tech/search-modules-shared/Icons';

import { Popover, PopoverRoot, PopoverTrigger } from '../Popover/Popover';
import { CyclingLoadingText } from '../CyclingLoadingText/CyclingLoadingText';
import { AiSuggestedPrompts } from '../AiSuggestedPrompts/AiSuggestedPrompts';
import { AiDraftExtractedFilters, AiExtractedFilters } from '../AiExtractedFilters/AiExtractedFilters';

import styles from './AiPromptInput.module.css';

type AiPromptInputProps = {
  isDropdownOpen: boolean;
  onClickHandler?: () => void;
  onChangeHandler: (content: string) => void;
  temporaryPromptValue: string;
  startAdornment?: React.ReactNode;
  endAdornmentRightSide?: React.ReactNode;
  shouldShowClearButton?: boolean;
  externalRef?: React.RefObject<HTMLDivElement>;
  preferencesInfoModalState?: ModalState;
  onSubmit?: () => void;
  onFocusChange?: (isFocused: boolean) => void;
  formId?: string;
  isAiPromptSearchDisabled?: boolean;
  hasPendingFilterChanges?: boolean;
  shouldUseDraftFilters?: boolean;
  onChipDelete?: (chip: FilterChip) => void;
  onApplyPendingFilters?: () => void;
  closeDropdown?: (e?: React.KeyboardEvent<HTMLDivElement>) => void;
  className?: string;
  contentClassName?: string;
  children?: React.ReactNode;
};

export const AiPromptInput = ({
  isDropdownOpen,
  closeDropdown,
  onClickHandler,
  onChangeHandler,
  temporaryPromptValue,
  startAdornment,
  endAdornmentRightSide,
  shouldShowClearButton = false,
  externalRef,
  preferencesInfoModalState,
  onSubmit,
  onFocusChange,
  isAiPromptSearchDisabled,
  hasPendingFilterChanges = false,
  shouldUseDraftFilters = false,
  onChipDelete,
  onApplyPendingFilters,
  formId = 'ai-prompt-form',
  className,
  contentClassName,
  children,
}: AiPromptInputProps) => {
  const { t } = useAppContext();
  const formRef = useRef<HTMLFormElement>(null);
  const promptValue = usePromptStore((state) => state.promptValue);
  const setPromptValue = usePromptStore((state) => state.setPromptValue);
  const setAutoCompleteOptionIndex = useAutoCompleteStore((state) => state.setAutoCompleteOptionIndex);
  const setNumberOfAutoCompleteOptions = useAutoCompleteStore((state) => state.setNumberOfAutoCompleteOptions);
  const setSelectedLokaliseOptionIndex = useAutoCompleteStore((state) => state.setSelectedLokaliseOptionIndex);

  const { isPending, mutate } = useAiSearch();
  const { isMobile } = useBreakpoint();

  const shouldShowExtractedFilters = useMemo(() => {
    return promptValue.trim().length > 0 && promptValue.trim() === temporaryPromptValue.trim() && !isPending && isDropdownOpen;
  }, [promptValue, temporaryPromptValue, isPending, isDropdownOpen]);

  const shouldShowSuggestedPrompts = useMemo(() => {
    return temporaryPromptValue.trim() === '' && !isPending && isDropdownOpen;
  }, [temporaryPromptValue, isPending, isDropdownOpen]);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (isAiPromptSearchDisabled && !hasPendingFilterChanges) return;
      externalRef?.current?.blur();
      if (isPending) return;

      if (hasPendingFilterChanges && promptValue.trim() === temporaryPromptValue.trim()) {
        onApplyPendingFilters?.();
        onSubmit?.();
        return;
      }

      if (temporaryPromptValue.trim().length < 3) return;

      setAutoCompleteOptionIndex(undefined);
      setNumberOfAutoCompleteOptions(undefined);
      setSelectedLokaliseOptionIndex(undefined);
      mutate(temporaryPromptValue);
      setPromptValue(temporaryPromptValue);
      onSubmit?.();
    },
    [
      externalRef,
      isPending,
      temporaryPromptValue,
      setAutoCompleteOptionIndex,
      setNumberOfAutoCompleteOptions,
      setSelectedLokaliseOptionIndex,
      setPromptValue,
      mutate,
      onSubmit,
      isAiPromptSearchDisabled,
      hasPendingFilterChanges,
      onApplyPendingFilters,
      promptValue,
    ],
  );

  const handleClearInput = useCallback(() => {
    onChangeHandler('');
    externalRef?.current?.focus();
  }, [onChangeHandler, externalRef]);

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      const content = e.currentTarget.textContent || '';
      onChangeHandler(content);
    },
    [onChangeHandler],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && (!e.shiftKey || isMobile)) {
        e.preventDefault();
        if (!temporaryPromptValue.trim()) {
          e.currentTarget.blur();
          return;
        }
        const form = e.currentTarget.closest('form');
        if (form) {
          form.requestSubmit();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        externalRef?.current?.blur();
        closeDropdown?.(e);
      }
    },
    [isMobile, temporaryPromptValue, closeDropdown, externalRef],
  );

  useEffect(() => {
    if (externalRef?.current && !isPending) {
      const element = externalRef.current;
      if (element.textContent !== temporaryPromptValue) {
        element.textContent = temporaryPromptValue;
      }
      if (element.textContent === '') {
        element.innerHTML = '';
      }
    }
  }, [temporaryPromptValue, externalRef, isPending]);

  const dropdownContent = shouldShowSuggestedPrompts ? (
    <AiSuggestedPrompts onSuggestionClick={onSubmit} />
  ) : shouldUseDraftFilters ? (
    <AiDraftExtractedFilters preferencesInfoModalState={preferencesInfoModalState} onChipDelete={onChipDelete} />
  ) : (
    <AiExtractedFilters preferencesInfoModalState={preferencesInfoModalState} onChipDelete={onChipDelete} />
  );

  const contentEditable = (
    <div
      className={styles.contentEditable}
      ref={externalRef}
      contentEditable="plaintext-only"
      onInput={handleInput}
      onClick={onClickHandler}
      onKeyDown={handleKeyDown}
      data-empty={!temporaryPromptValue}
      data-expanded={isDropdownOpen}
      data-placeholder={t('search.aiSearch.aiMode.promptInput.placeholder')}
      role="textbox"
      aria-label={t('search.aiSearch.aiMode.promptInput.placeholder')}
      aria-multiline="true"
      enterKeyHint="search"
      onFocus={() => {
        onFocusChange?.(true);
        onClickHandler?.();
      }}
      onBlur={() => {
        onFocusChange?.(false);
      }}
    />
  );

  const formBody = (
    <>
      {Boolean(startAdornment) && startAdornment}
      {isPending ? (
        <CyclingLoadingText />
      ) : isMobile ? (
        contentEditable
      ) : (
        <PopoverRoot
          open={isDropdownOpen}
          onOpenChange={(open, eventDetails) => {
            if (!open && preferencesInfoModalState?.isOpen) {
              eventDetails.cancel();
              return;
            }
            if (!open && eventDetails.reason !== 'trigger-press') closeDropdown?.();
          }}
        >
          <PopoverTrigger render={contentEditable} />
          {(shouldShowSuggestedPrompts || shouldShowExtractedFilters) && (
            <Popover
              aria-label={t('search.aiSearch.aiMode.promptInput.placeholder')}
              className={styles.dropdownPopover}
              finalFocus={false}
              initialFocus={false}
              portalContainer={formRef}
              sideOffset={1}
            >
              {dropdownContent}
            </Popover>
          )}
        </PopoverRoot>
      )}
      {(Boolean(endAdornmentRightSide) || shouldShowClearButton) && (
        <div className={styles.endAdornmentsContainer}>
          {shouldShowClearButton && (
            <IconButton
              icon={<CloseIcon />}
              aria-label="Clear input"
              variant="tertiaryAlt"
              shape="square"
              size="xsmall"
              onClick={handleClearInput}
            />
          )}
          {endAdornmentRightSide}
        </div>
      )}
      {isMobile && (shouldShowSuggestedPrompts || shouldShowExtractedFilters) && (
        <div className={styles.dropdownContainer}>{dropdownContent}</div>
      )}
    </>
  );

  return (
    <form
      ref={formRef}
      className={cn(styles.form, className)}
      id={formId}
      onSubmit={handleSubmit}
      onReset={() => setPromptValue('')}
      aria-busy={isPending}
    >
      {contentClassName ? <div className={contentClassName}>{formBody}</div> : formBody}
      {children}
    </form>
  );
};
